import { useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { CandidateFamily, GroupableProfile } from "types/accountGroup";
import { useAccountGroups } from "./useAccountGroups";

function ProfileRow({
  profile,
  checked,
  disabled,
  onChange,
}: {
  profile: GroupableProfile;
  checked?: boolean;
  disabled?: boolean;
  onChange?: (checked: boolean) => void;
}) {
  const content = (
    <Stack direction="row" spacing={1.5} alignItems="center" width="100%">
      <Avatar src={profile.picture_url || undefined} alt={profile.name}>
        {profile.name?.[0]}
      </Avatar>
      <Box flex={1} minWidth={0}>
        <Typography fontWeight={600}>{profile.name}</Typography>
        <Typography variant="body2">{profile.email}</Typography>
        <Typography variant="caption" sx={{ textTransform: "capitalize" }}>
          {profile.role}
        </Typography>
      </Box>
      <Chip
        size="small"
        color={profile.group_id ? "warning" : "default"}
        label={
          profile.group_id
            ? `Already in group ${profile.group_name || profile.group_id}`
            : "Not grouped"
        }
      />
    </Stack>
  );
  return onChange ? (
    <FormControlLabel
      sx={{ display: "flex", m: 0, py: 1, width: "100%" }}
      control={
        <Checkbox
          checked={Boolean(checked)}
          disabled={disabled}
          onChange={(_, value) => onChange(value)}
        />
      }
      label={content}
    />
  ) : (
    <Box py={1}>{content}</Box>
  );
}

function CandidateCard({
  family,
  saving,
  onCreate,
  onMerge,
}: {
  family: CandidateFamily;
  saving: boolean;
  onCreate: (userIds: string[]) => Promise<boolean>;
  onMerge: (family: CandidateFamily, userIds: string[]) => Promise<boolean>;
}) {
  const available = family.profiles.filter((profile) => !profile.group_id);
  const [selected, setSelected] = useState<string[]>(
    available.map((profile) => profile.user_id),
  );
  const conflictCount = family.profiles.length - available.length;
  const canMerge = Boolean(family.existing_group_id) && selected.length > 0;
  const canCreate = !family.existing_group_id && selected.length >= 2;
  const conflictingGroups = Array.from(
    new Set(
      family.profiles
        .map((profile) => profile.group_id)
        .filter((groupId): groupId is string => Boolean(groupId)),
    ),
  );

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={1.5}>
          <Box>
            <Typography variant="h6">Candidate family</Typography>
            {family.email && (
              <Typography variant="body2">{family.email}</Typography>
            )}
          </Box>
          {family.profiles.map((profile) => (
            <ProfileRow
              key={profile.user_id}
              profile={profile}
              checked={selected.includes(profile.user_id)}
              disabled={Boolean(profile.group_id)}
              onChange={(checked) =>
                setSelected((current) =>
                  checked
                    ? [...current, profile.user_id]
                    : current.filter((id) => id !== profile.user_id),
                )
              }
            />
          ))}
          {conflictingGroups.length > 1 && (
            <Alert severity="error">
              Profiles in this email family belong to different groups and cannot
              be merged automatically.
            </Alert>
          )}
          {family.existing_group_id && conflictingGroups.length === 1 && (
            <Alert severity="info">
              An existing group was found for this email alias. Ungrouped
              profiles can be merged into it.
            </Alert>
          )}
          {conflictCount > 0 && !family.existing_group_id && (
            <Alert severity="warning">
              {conflictCount} profile{conflictCount === 1 ? " is" : "s are"} in
              an existing group and cannot be selected.
            </Alert>
          )}
          {!canMerge && !canCreate && (
            <Alert severity="info">
              {family.existing_group_id
                ? "Select at least one ungrouped profile to merge into the existing group."
                : "Select at least two ungrouped profiles to create a group."}
            </Alert>
          )}
          <Box>
            <Button
              variant="contained"
              disabled={saving || (!canMerge && !canCreate)}
              onClick={() =>
                canMerge ? onMerge(family, selected) : onCreate(selected)
              }
            >
              {canMerge ? "Merge into existing group" : "Create group"}
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export function AccountGroupsPanel() {
  const {
    groups,
    families,
    loadingGroups,
    lookingUp,
    saving,
    error,
    hasLookedUp,
    lookup,
    createGroup,
    mergeIntoGroup,
    createAllGroups,
    updateGroup,
    deleteGroup,
  } = useAccountGroups();
  const [email, setEmail] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editMembers, setEditMembers] = useState<string[]>([]);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const profiles = useMemo(() => {
    const byId = new Map<string, GroupableProfile>();
    families.forEach((family) =>
      family.profiles.forEach((profile) => byId.set(profile.user_id, profile)),
    );
    groups.forEach((group) =>
      group.members.forEach((profile) =>
        byId.set(profile.user_id, {
          ...profile,
          group_id: profile.group_id || group.id,
        }),
      ),
    );
    return Array.from(byId.values());
  }, [families, groups]);

  const creatableFamilies = useMemo(
    () =>
      families.filter((family) => {
        const ungroupedCount = family.profiles.filter(
          (profile) => !profile.group_id,
        ).length;
        return (
          ungroupedCount >= 2 ||
          (ungroupedCount > 0 && Boolean(family.existing_group_id))
        );
      }),
    [families],
  );

  const startEditing = (groupId: string, memberIds: string[]) => {
    setEditingId(groupId);
    setEditMembers(memberIds);
  };

  return (
    <Stack spacing={3} py={2}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
        <Button
          variant="outlined"
          disabled={lookingUp}
          onClick={() => lookup()}
        >
          Lookup all groupable users
        </Button>
        <TextField
          size="small"
          type="email"
          label="Email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <Button
          variant="contained"
          disabled={lookingUp || !email.trim()}
          onClick={() => lookup(email.trim())}
        >
          Lookup email
        </Button>
        {lookingUp && <CircularProgress size={24} aria-label="Looking up" />}
      </Stack>

      {!lookingUp && creatableFamilies.length > 0 && (
        <Box>
          <Button
            variant="contained"
            disabled={saving}
            onClick={() => createAllGroups()}
          >
            Create All
          </Button>
        </Box>
      )}

      {error && <Alert severity="error">{error}</Alert>}

      {hasLookedUp && !lookingUp && families.length === 0 && (
        <Alert severity="info">No groupable users found.</Alert>
      )}
      {families.map((family) => (
        <CandidateCard
          key={family.id}
          family={family}
          saving={saving}
          onCreate={createGroup}
          onMerge={mergeIntoGroup}
        />
      ))}

      <Divider />
      <Typography variant="h5">Existing groups</Typography>
      {loadingGroups ? (
        <CircularProgress aria-label="Loading groups" />
      ) : groups.length === 0 ? (
        <Alert severity="info">No linked account groups exist yet.</Alert>
      ) : (
        groups.map((group) => {
          const editing = editingId === group.id;
          const memberIds = group.members.map((member) => member.user_id);
          return (
            <Card variant="outlined" key={group.id}>
              <CardContent>
                <Stack spacing={1.5}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="h6">
                      Group {group.id} ({group.members.length} profiles)
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Button
                        onClick={() => startEditing(group.id, memberIds)}
                      >
                        Edit members
                      </Button>
                      <Button
                        color="error"
                        onClick={() => setDeleteId(group.id)}
                      >
                        Delete
                      </Button>
                    </Stack>
                  </Stack>
                  {(editing ? profiles : group.members).map((profile) => {
                    const belongsElsewhere =
                      Boolean(profile.group_id) &&
                      profile.group_id !== group.id &&
                      !memberIds.includes(profile.user_id);
                    return (
                      <ProfileRow
                        key={profile.user_id}
                        profile={profile}
                        checked={
                          editing
                            ? editMembers.includes(profile.user_id)
                            : undefined
                        }
                        disabled={editing && belongsElsewhere}
                        onChange={
                          editing
                            ? (checked) =>
                                setEditMembers((current) =>
                                  checked
                                    ? [...current, profile.user_id]
                                    : current.filter(
                                        (id) => id !== profile.user_id,
                                      ),
                                )
                            : undefined
                        }
                      />
                    );
                  })}
                  {editing && editMembers.length < 2 && (
                    <Alert severity="warning">
                      A group must contain at least two profiles.
                    </Alert>
                  )}
                  {editing && (
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="contained"
                        disabled={saving || editMembers.length < 2}
                        onClick={async () => {
                          if (await updateGroup(group.id, editMembers)) {
                            setEditingId(null);
                          }
                        }}
                      >
                        Save members
                      </Button>
                      <Button onClick={() => setEditingId(null)}>Cancel</Button>
                    </Stack>
                  )}
                </Stack>
              </CardContent>
            </Card>
          );
        })
      )}

      <Dialog open={Boolean(deleteId)} onClose={() => setDeleteId(null)}>
        <DialogTitle>Delete linked account group?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Profiles will no longer be available through account switching.
            This does not delete the user profiles.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={saving}
            onClick={async () => {
              if (deleteId && (await deleteGroup(deleteId))) {
                setDeleteId(null);
              }
            }}
          >
            Delete group
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
